<template>
  <div class="relative">
    <div>
      <div
        class="mb-1 d-flex justify-content-between align-items-center flex-wrap mt-3"
      >
        <div class="d-flex flex-wrap align-items-center">
          <div>
            <b>Technician</b>
            <span v-if="!showAll" class="hint-text whitespace-nowrap"
              >(Only Qualified Technicians for Selected Service Category)</span
            >
          </div>
        </div>
        <Checkbox
          v-model="showAll"
          label="Include All Technicians"
          class="all-techs whitespace-nowrap mb-0"
        ></Checkbox>
      </div>
      <div v-if="warningMessage" class="warning-message whitespace-nowrap">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        {{ warningMessage }}
      </div>
      <SelectInput
        :model-value="modelValue"
        placeholder="Auto Assign"
        :options="eligibleTechs"
        :filter="true"
        filter-placeholder="Search technicians..."
        :body-render="false"
        :multiselect="isMultiTechEnabled"
        :max-selections="isMultiTechEnabled ? MAX_TECHS : undefined"
        :helper-text="limitHelperText"
        data-cy="technician-select"
        @update:modelValue="handleTechSelect"
      />
      <div v-if="capturePinnedReason" class="mt-2">
        <span class="label"
          >Reason for Switching <em>(Pinning)</em> Technician</span
        >
        <TextArea v-model="pinnedReasonText" data-cy="pinned-reason"></TextArea>
      </div>
      <div v-else-if="showPinnedReason" class="mt-2 d-flex">
        <span class="font-bold whitespace-nowrap">Pinned Reason:</span>
        <TruncateText :text="pinnedReason" class="ml-1"></TruncateText>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import Checkbox from "@/components/Checkbox.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import TextArea from "@/components/TextArea.vue";
import TruncateText from "@/components/TruncateText.vue";
import { useMultiTechSelect } from "@/composables/useMultiTechSelect";
import { fullName } from "@/mixins";
import { Technician } from "@/typings/technician";

const props = withDefaults(
  defineProps<{
    serviceCategoryId?: number | null;
    modelValue?: number | number[] | null;
    pinnedTechnicianId?: number | null;
    pinnedReason?: string;
    noSave?: boolean;
  }>(),
  {
    serviceCategoryId: null,
    modelValue: null,
    pinnedTechnicianId: null,
    pinnedReason: "N/A",
    noSave: false
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: number | number[] | null];
  "update-pinned-reason": [value: string];
}>();

const store = useStore();
const showAll = ref(false);
const pinnedReasonText = ref(props.pinnedReason);

const selectedTechIds = computed(() => {
  if (Array.isArray(props.modelValue)) return props.modelValue;
  return [];
});

const { isMultiTechEnabled, limitHelperText, MAX_TECHS } =
  useMultiTechSelect(selectedTechIds);

onMounted(() => {
  if (store.state.technicians.length === 0) {
    store.dispatch("fetchTechnicians");
  }
});

function isPinned(technician: Technician): boolean {
  return technician.user_id === props.pinnedTechnicianId;
}

function getLabel(technician: Technician): string {
  let label = fullName(technician);
  if (isPinned(technician)) {
    label += " (Pinned)";
  }
  return label;
}

const eligibleTechs = computed(() => {
  const technicians: Technician[] = store.state.technicians;
  const techOptions = technicians.map((tech) => ({
    ...tech,
    label: getLabel(tech),
    value: tech.user_id
  }));

  if (!props.serviceCategoryId || showAll.value) {
    return techOptions;
  }

  const currentIds = Array.isArray(props.modelValue)
    ? props.modelValue
    : props.modelValue
    ? [props.modelValue]
    : [];

  return techOptions.filter(
    (tech) =>
      (tech.service_categories_ids || []).includes(props.serviceCategoryId!) ||
      currentIds.includes(tech.user_id)
  );
});

const warningMessage = computed(() => {
  if (isMultiTechEnabled.value && Array.isArray(props.modelValue)) {
    const hasMissing = props.modelValue.some((uid) => {
      const tech = eligibleTechs.value.find((t) => t.user_id === uid);
      return (
        tech &&
        !(tech.service_categories_ids || []).includes(props.serviceCategoryId!)
      );
    });
    return hasMissing ? "Technician Missing Service Category" : "";
  }

  const selectedTech = eligibleTechs.value.find(
    (tech) => tech.user_id === props.modelValue
  );
  if (
    selectedTech &&
    !(selectedTech.service_categories_ids || []).includes(
      props.serviceCategoryId!
    )
  ) {
    return "Technician Missing Service Category";
  }
  return "";
});

const primaryTechId = computed(() => {
  if (Array.isArray(props.modelValue)) return props.modelValue[0] ?? null;
  return props.modelValue;
});

const capturePinnedReason = computed(() => {
  if (props.noSave) return false;
  const hasSelection = Array.isArray(props.modelValue)
    ? props.modelValue.length > 0
    : !!props.modelValue;
  return hasSelection && primaryTechId.value !== props.pinnedTechnicianId;
});

const showPinnedReason = computed(() => {
  const hasSelection = Array.isArray(props.modelValue)
    ? props.modelValue.length > 0
    : !!props.modelValue;
  return hasSelection && primaryTechId.value === props.pinnedTechnicianId;
});

function handleTechSelect(val: number | number[] | null) {
  if (Array.isArray(val) && val.length > MAX_TECHS) return;
  emit("update:modelValue", val);
}

watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      if (primaryTechId.value === props.pinnedTechnicianId) {
        pinnedReasonText.value = props.pinnedReason;
      } else {
        pinnedReasonText.value = "";
      }
    }
  }
);

watch(pinnedReasonText, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    emit("update-pinned-reason", newValue);
  }
});
</script>

<style lang="scss" scoped>
.all-techs {
  font-size: 0.9em;
}
.warning-message {
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--warning-yellow);
  padding: 5px;
  margin-bottom: 0.5rem;
  text-align: center;
}
</style>
